'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { audioStoryKeys } from '../queryKeys';
import { toggleFavoriteAudioStory } from '../actions/toggleFavorite';
import { useToast } from '@/shared/components/ui/use-toast';
import type { AudioStory } from '../types';

type ListCache = { audioStories: AudioStory[]; total: number };

export function useToggleAudioStoryFavorite(familyId?: string | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const t = useTranslations('audioStories.list.favorites');

  return useMutation({
    mutationFn: (audioStoryId: string) => toggleFavoriteAudioStory(audioStoryId),

    onMutate: async (audioStoryId: string) => {
      // Cancel any in-flight queries to avoid race conditions
      await queryClient.cancelQueries({ queryKey: audioStoryKeys.all });

      // Snapshot previous caches for rollback
      const snapshots: Array<{ queryKey: readonly unknown[]; data: unknown }> = [];

      // Helper: flip favorite on a single AudioStory object
      const flipStory = (s: AudioStory): AudioStory =>
        s.id === audioStoryId
          ? { ...s, isFavorite: !s.isFavorite, favoritedAt: !s.isFavorite ? new Date().toISOString() : null }
          : s;

      // Update all list caches (both all and favorites-only)
      const listKeys = [
        familyId ? audioStoryKeys.list(familyId) : null,
        familyId ? audioStoryKeys.list(familyId, { onlyFavorites: true }) : null,
      ].filter(Boolean) as ReturnType<typeof audioStoryKeys.list>[];

      for (const key of listKeys) {
        const prev = queryClient.getQueryData<ListCache>(key);
        if (prev) {
          snapshots.push({ queryKey: key, data: prev });
          queryClient.setQueryData<ListCache>(key, {
            ...prev,
            audioStories: prev.audioStories.map(flipStory),
          });
        }
      }

      // Update detail cache
      const detailKey = audioStoryKeys.detail(audioStoryId);
      const prevDetail = queryClient.getQueryData<AudioStory | null>(detailKey);
      if (prevDetail) {
        snapshots.push({ queryKey: detailKey, data: prevDetail });
        queryClient.setQueryData<AudioStory>(detailKey, flipStory(prevDetail));
      }

      return { snapshots };
    },

    onError: (_err, _audioStoryId, context) => {
      // Revert all optimistic updates
      if (context?.snapshots) {
        for (const { queryKey, data } of context.snapshots) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast({
        variant: 'destructive',
        title: t('errorTitle'),
        description: t('toggleError'),
      });
    },

    onSuccess: (result, audioStoryId) => {
      if (!result.success) {
        // Server returned an error — invalidate to restore correct state
        queryClient.invalidateQueries({ queryKey: audioStoryKeys.all });
        toast({
          variant: 'destructive',
          title: t('errorTitle'),
          description: result.error ?? t('toggleError'),
        });
        return;
      }
      // Reconcile caches with server truth
      const { isFavorite, favoritedAt } = result.data;

      const applyServerTruth = (s: AudioStory): AudioStory =>
        s.id === audioStoryId ? { ...s, isFavorite, favoritedAt } : s;

      const listKeys = [
        familyId ? audioStoryKeys.list(familyId) : null,
        familyId ? audioStoryKeys.list(familyId, { onlyFavorites: true }) : null,
      ].filter(Boolean) as ReturnType<typeof audioStoryKeys.list>[];

      for (const key of listKeys) {
        const cache = queryClient.getQueryData<ListCache>(key);
        if (cache) {
          queryClient.setQueryData<ListCache>(key, {
            ...cache,
            audioStories: cache.audioStories.map(applyServerTruth),
          });
        }
      }

      const detailKey = audioStoryKeys.detail(audioStoryId);
      const detail = queryClient.getQueryData<AudioStory | null>(detailKey);
      if (detail) {
        queryClient.setQueryData<AudioStory>(detailKey, applyServerTruth(detail));
      }
    },
  });
}
